import React, { useState, useCallback } from 'react';
import TEMPLATES, { JH_TEMPLATES, V_TEMPLATES } from '../templates';
import ImageUploader from './ImageUploader';

const DEFAULT_STATE = {
  templateId: '1',
  headline: 'FOLLOW THIS',
  personImage: '',
  subjectImage: '',
  bgImage: '',
  bgColor: '#1a1a2e',
  accentColor: '#FFD700',
  highlightWords: '',
  emphasisWord: 'NOT',
  labels: ['Urgency Activation', 'Intolerable Standards', 'Revenue-First Thinking', 'Relentless Iteration', 'Accountability Pressure', 'Anti-Comfort Bias'],
  cardTitle: 'BOOK FUNNEL',
  cardItems: ['Lead Magnet', 'Landing Page', 'Email Sequence', 'Upsell Offer'],
  metrics: [
    { label: 'Ad Spend', value: '$12,400' },
    { label: 'Revenue', value: '$184,500' },
    { label: 'ROAS', value: '14.8x' },
  ],
  tiers: [
    { rank: 'S', color: '#E53935', items: ['VSL Funnel'] },
    { rank: 'A', color: '#FB8C00', items: ['Webinar', 'Book Funnel'] },
    { rank: 'B', color: '#FDD835', items: ['Quiz Funnel', 'Lead Magnet'] },
    { rank: 'C', color: '#43A047', items: ['Blog Posts'] },
    { rank: 'D', color: '#1E88E5', items: ['Cold Email'] },
  ],
  steps: [
    { icon: 'target', label: 'Identify ICP' },
    { icon: 'brain', label: 'Craft Offer' },
    { icon: 'money', label: 'Set Budget' },
    { icon: 'launch', label: 'Launch Ads' },
    { icon: 'chart', label: 'Track Data' },
    { icon: 'gear', label: 'Optimize' },
  ],
  chartBars: [
    { label: '2020', value: 45, color: '#444' },
    { label: '2021', value: 55, color: '#555' },
    { label: '2022', value: 70, color: '#666' },
    { label: '2023', value: 60, color: '#E53935' },
    { label: '2024', value: 35, color: '#E53935' },
  ],
};

export default function ThumbnailPreview() {
  const [state, setState] = useState(DEFAULT_STATE);
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);

  const set = useCallback((key, val) => {
    setState(prev => ({ ...prev, [key]: val }));
  }, []);

  const tmpl = TEMPLATES[state.templateId];
  const TemplateComponent = tmpl?.component;
  const isJH = tmpl?.style === 'jh';

  // Build props for the React preview component
  const previewProps = {
    headline: state.headline,
    personImage: state.personImage ? `/uploads/${state.personImage}` : undefined,
    subjectImage: state.subjectImage ? `/uploads/${state.subjectImage}` : undefined,
    bgImage: state.bgImage ? `/uploads/${state.bgImage}` : undefined,
    bgColor: state.bgColor,
    accentColor: state.accentColor,
    highlightWords: state.highlightWords,
    emphasisWord: state.emphasisWord,
    labels: state.labels,
    cardTitle: state.cardTitle,
    cardItems: state.cardItems,
    metrics: state.metrics,
    tiers: state.tiers,
    steps: state.steps,
    chartBars: state.chartBars,
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setLastGenerated(null);
    try {
      const res = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateId: state.templateId, data: state }),
      });
      const json = await res.json();
      if (json.success) {
        setLastGenerated(json);
      }
    } catch (err) {
      console.error('Generate failed:', err);
    }
    setGenerating(false);
  };

  // Dynamic controls based on template
  const renderTemplateControls = () => {
    const tid = state.templateId;

    // Template 1: highlight words
    if (tid === '1') return (
      <div className="control-group">
        <label className="control-label">Highlight Words (comma-separated)</label>
        <input className="control-input" value={state.highlightWords}
          onChange={e => set('highlightWords', e.target.value)}
          placeholder="e.g. FOLLOW, THIS" />
      </div>
    );

    // Template 2: labels
    if (tid === '2') return (
      <div className="control-group">
        <label className="control-label">Labels ({state.labels.length})</label>
        {state.labels.map((label, i) => (
          <div key={i} className="list-input-row">
            <input className="control-input" value={label}
              onChange={e => {
                const next = [...state.labels];
                next[i] = e.target.value;
                set('labels', next);
              }} />
            <button className="list-remove-btn"
              onClick={() => set('labels', state.labels.filter((_, j) => j !== i))}>−</button>
          </div>
        ))}
        <button className="list-add-btn" style={{ width: '100%' }}
          onClick={() => set('labels', [...state.labels, 'New Label'])}>+ Add Label</button>
      </div>
    );

    // Template 3: card title + items
    if (tid === '3') return (
      <>
        <div className="control-group">
          <label className="control-label">Card Title</label>
          <input className="control-input" value={state.cardTitle}
            onChange={e => set('cardTitle', e.target.value)} />
        </div>
        <div className="control-group">
          <label className="control-label">Card Items ({state.cardItems.length})</label>
          {state.cardItems.map((item, i) => (
            <div key={i} className="list-input-row">
              <input className="control-input" value={item}
                onChange={e => {
                  const next = [...state.cardItems];
                  next[i] = e.target.value;
                  set('cardItems', next);
                }} />
              <button className="list-remove-btn"
                onClick={() => set('cardItems', state.cardItems.filter((_, j) => j !== i))}>−</button>
            </div>
          ))}
          <button className="list-add-btn" style={{ width: '100%' }}
            onClick={() => set('cardItems', [...state.cardItems, 'New Item'])}>+ Add Item</button>
        </div>
      </>
    );

    // Template 4: metrics
    if (tid === '4') return (
      <div className="control-group">
        <label className="control-label">Metrics ({state.metrics.length})</label>
        {state.metrics.map((m, i) => (
          <div key={i} className="metric-row">
            <input className="control-input" placeholder="Label" value={m.label}
              onChange={e => {
                const next = [...state.metrics];
                next[i] = { ...next[i], label: e.target.value };
                set('metrics', next);
              }} />
            <input className="control-input" placeholder="Value" value={m.value}
              onChange={e => {
                const next = [...state.metrics];
                next[i] = { ...next[i], value: e.target.value };
                set('metrics', next);
              }} />
            <button className="list-remove-btn"
              onClick={() => set('metrics', state.metrics.filter((_, j) => j !== i))}>−</button>
          </div>
        ))}
        <button className="list-add-btn" style={{ width: '100%' }}
          onClick={() => set('metrics', [...state.metrics, { label: 'Metric', value: '$0' }])}>+ Add Metric</button>
      </div>
    );

    // Template 9: emphasis word
    if (tid === '9') return (
      <div className="control-group">
        <label className="control-label">Emphasis Word</label>
        <input className="control-input" value={state.emphasisWord}
          onChange={e => set('emphasisWord', e.target.value)}
          placeholder="e.g. NOT" />
      </div>
    );

    // Template 11: uses bgImage instead of subjectImage
    if (tid === '11') return null; // bgImage uploader handled in image section

    // Template 12: chart bars
    if (tid === '12') return (
      <div className="control-group">
        <label className="control-label">Chart Bars ({state.chartBars.length})</label>
        {state.chartBars.map((bar, i) => (
          <div key={i} className="metric-row">
            <input className="control-input" placeholder="Label" value={bar.label}
              onChange={e => {
                const next = [...state.chartBars];
                next[i] = { ...next[i], label: e.target.value };
                set('chartBars', next);
              }} />
            <input className="control-input" type="number" placeholder="Value" value={bar.value}
              onChange={e => {
                const next = [...state.chartBars];
                next[i] = { ...next[i], value: Number(e.target.value) };
                set('chartBars', next);
              }} />
            <button className="list-remove-btn"
              onClick={() => set('chartBars', state.chartBars.filter((_, j) => j !== i))}>−</button>
          </div>
        ))}
        <button className="list-add-btn" style={{ width: '100%' }}
          onClick={() => set('chartBars', [...state.chartBars, { label: 'Year', value: 50, color: '#666' }])}>+ Add Bar</button>
      </div>
    );

    return null;
  };

  return (
    <div className="preview-layout">
      {/* ─── Controls Sidebar ─── */}
      <div className="controls-sidebar">
        {/* Template Selector */}
        <div className="control-group">
          <div className="template-group-title jh">Jeremy Haynes Style</div>
          <div className="template-grid">
            {JH_TEMPLATES.map(t => (
              <button key={t.id}
                className={`template-btn ${state.templateId === t.id ? 'active' : ''}`}
                onClick={() => set('templateId', t.id)}>
                {t.id}. {t.name}
              </button>
            ))}
          </div>

          <div className="template-group-title v">Veritasium Style</div>
          <div className="template-grid">
            {V_TEMPLATES.map(t => (
              <button key={t.id}
                className={`template-btn ${state.templateId === t.id ? 'active' : ''}`}
                onClick={() => set('templateId', t.id)}>
                {t.id}. {t.name}
              </button>
            ))}
          </div>
        </div>

        {/* Headline */}
        <div className="control-group">
          <label className="control-label">Headline</label>
          <input className="control-input" value={state.headline}
            onChange={e => set('headline', e.target.value)}
            placeholder="Enter headline text..." />
        </div>

        {/* Template-specific controls */}
        {renderTemplateControls()}

        {/* Image Uploaders */}
        {tmpl?.needsPerson && (
          <ImageUploader label="Person Image"
            value={state.personImage}
            onChange={v => set('personImage', v)} />
        )}
        {tmpl?.needsSubject && (
          <ImageUploader label="Subject Image"
            value={state.subjectImage}
            onChange={v => set('subjectImage', v)} />
        )}
        {state.templateId === '11' && (
          <ImageUploader label="Background Photo"
            value={state.bgImage}
            onChange={v => set('bgImage', v)} />
        )}

        {/* Colors */}
        <div className="control-group">
          <label className="control-label">Colors</label>
          <div className="color-row">
            <input type="color" className="color-swatch"
              value={state.bgColor} onChange={e => set('bgColor', e.target.value)} />
            <input className="control-input" value={state.bgColor}
              onChange={e => set('bgColor', e.target.value)} placeholder="#000000" />
          </div>
          <div className="color-row" style={{ marginTop: 6 }}>
            <input type="color" className="color-swatch"
              value={state.accentColor} onChange={e => set('accentColor', e.target.value)} />
            <input className="control-input" value={state.accentColor}
              onChange={e => set('accentColor', e.target.value)} placeholder="Accent" />
          </div>
        </div>

        {/* Generate */}
        <button className="generate-btn" onClick={handleGenerate} disabled={generating}>
          {generating ? 'Generating...' : 'Generate PNG'}
        </button>

        {lastGenerated && (
          <div style={{ textAlign: 'center', fontSize: 13, color: '#888' }}>
            <a href={lastGenerated.url} target="_blank" rel="noreferrer"
              style={{ color: '#E53935', fontWeight: 700 }}>
              Download {lastGenerated.filename}
            </a>
            <div style={{ marginTop: 4 }}>
              {(lastGenerated.size / 1024).toFixed(0)} KB
            </div>
          </div>
        )}
      </div>

      {/* ─── Preview Panel ─── */}
      <div className="preview-panel">
        <div className="preview-frame">
          {TemplateComponent && <TemplateComponent {...previewProps} />}
        </div>
        <div className="preview-info">
          1280 × 720 &middot; Template {state.templateId}: {tmpl?.name}
        </div>
      </div>
    </div>
  );
}
